import React, { useState } from 'react';
import axios from 'axios';

const PlagiarismChecker = ({ user }) => {
    if (!user || user.role !== 'admin') {
        return (
            <div style={{padding: '40px', textAlign: 'center'}}>
                <h2>Access Denied</h2>
                <p>This tool is restricted to System Administrators.</p>
            </div>
        );
    }
    const [text, setText] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);

    const handleFileCheck = async () => {
        if (!file) return alert("Please select a PDF file");

        setLoading(true);
        setResults(null);
        const formData = new FormData();
        formData.append('thesisFile', file);

        try {
            const res = await axios.post('/api/plagiarism/check-pdf', formData);
            setResults(res.data);

            if (res.data.topMatches.length === 0) {
                alert("Scan complete: No matches found above the threshold.");
            }
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || "Error checking PDF. Check if the backend is running.");
        } finally {
            setLoading(false);
        }
    };

    const checkText = async () => {
        if (!text.trim()) return alert("Please paste some text first.");
        setLoading(true);
        setResults(null);
        try {
            const res = await axios.post('/api/plagiarism/check', { text: text });
            setResults(res.data);
        } catch (err) { 
            console.dir(err); 
            alert(`Error checking text: ${err.response?.data?.error || err.response?.data?.message || "Check console for details"}`); 
        } finally {
            setLoading(false);
        }
    };

    const statusColor = results?.overallStatus === 'Original'
        ? '#198754'
        : results?.overallStatus === 'Partial Match'
            ? '#fd7e14'
            : '#dc3545';

    return (
        <div style={{ maxWidth: '800px', margin: '40px auto', padding: '20px' }}>
            <h2>Similarity Checker</h2>
            <p style={{ color: '#666' }}>
                Paste an abstract or any thesis text, or upload a PDF file, to compare it against thesis projects stored in the system.
            </p>
            
            <p style={{ color: '#666' }}>
                        <strong>Note:</strong> This only detects similarities with existing thesis projects within the system.
            </p>
            
            <textarea 
                style={textAreaStyle} 
                placeholder="Paste an abstract, chapter excerpt, or other thesis text here..." 
                value={text} 
                onChange={(e) => setText(e.target.value)}
            />

            <button onClick={checkText} disabled={loading} style={btnStyle}>
                {loading ? 'Analyzing...' : 'Check Abstract / Text'}
            </button>

            <div style={{ margin: '20px 0', border: '1px dashed #ccc', padding: '20px', borderRadius: '8px' }}>
                <h4>Check via PDF Upload</h4>
                <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} />
                <button onClick={handleFileCheck} disabled={loading} style={{...btnStyle, marginTop: '10px'}}>
                    {loading ? 'Scanning PDF...' : 'Scan PDF File'}
                </button>
            </div>

            {results && (
                <div style={resultBox}>
                    <h3>
                        Result: <span style={{ color: statusColor }}>{results.overallStatus}</span>
                    </h3>
                    <p style={{ marginTop: '8px', color: '#555' }}>
                        Compared {results.sourceType === 'pdf' ? 'the uploaded PDF' : 'the pasted text'} against the archive
                        {results.analyzedWordCount ? ` using ${results.analyzedWordCount} meaningful words.` : '.'}
                    </p>
                    {results.topMatches.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {results.topMatches.map((m, i) => (
                                <li key={i} style={listItem}>
                                    <span style={{ 
                                        fontWeight: 'bold', 
                                        color: m.similarity > 70 ? '#dc3545' : m.similarity > 40 ? '#fd7e14' : '#28a745' 
                                    }}>
                                        {m.similarity}% Match
                                    </span> with thesis: <em>{m.title}</em>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No significant matches found in the archive, including records that only have abstracts.</p>
                    )}
                </div>
            )}
        </div>
    );
};

const textAreaStyle = { width: '100%', height: '200px', padding: '15px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' };
const btnStyle = { padding: '12px 24px', background: '#800000', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' };
const resultBox = { marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '5px solid #800000' };
const listItem = { padding: '10px 0', borderBottom: '1px solid #eee' };

export default PlagiarismChecker;
